import React, { ChangeEvent, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
} from "@material-ui/core";
import { GrClose } from "react-icons/gr";
import HttpClient, {
  HttpGetOutput,
  HttpMutationOutput,
} from "../../lib/HttpClient";
import { useSnackbarContext } from "../../contexts/Snackbar";
import utils from "../../Utils";
import ImageFileIcon from "../../img/image-file-icon.png";
import { ReactComponent as PDFIcon } from "../../img/icons/icon_pdf.svg";
import ConfirmDeleteReportDialog from "./ConfirmDeleteReportDialog";
import DeleteOutlinedIcon from "@material-ui/icons/DeleteOutlined";

interface UploadDialogInterface {
  closeFn: () => void;
  patientId: String;
  isFromAppointment: Boolean;
  appointmentId?: String;
}

const REPORT_SECTIONS = [
  { label: "Pathology", apiType: "Pathology" },
  { label: "Imaging", apiType: "Bodyscan" },
  { label: "Neurophysiology", apiType: "Neurophysiology" },
  { label: "Genetics", apiType: "Genetics" },
  { label: "Psychology", apiType: "Psychology" },
] as const;

type SectionLabel = (typeof REPORT_SECTIONS)[number]["label"];

const allowedFileTypes = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "application/pdf",
];

const invalid_report_err_msg =
  "You can only upload photos (PNG, JPG, JPEG) or PDF files. Audio and Video files are not supported. Please try again.";

export default function UploadReportDialog({
  closeFn,
  patientId,
  isFromAppointment,
  appointmentId,
}: UploadDialogInterface) {
  const userProfile = utils.retrieveUserProfile();
  const getReportUploadUrl =
    "/api/v1/users/profile/" + patientId + "/reports/" + userProfile.clinicId;
  const getReportUploadUrlFromAppointment =
    "/api/v1/users/reports?appointmentId=" + appointmentId;

  const getReportsUrl = "/api/v1/users/reports?appointmentId=" + appointmentId;

  let [show, setShow] = useState(true);
  let [uploadingSection, setUploadingSection] = useState<SectionLabel | null>(
    null,
  );
  let [isGetReportsFetching, setIsGetReportsFetching] = useState(false);

  let [uploadedReportsList, setUploadedReportsList] = useState<any>();

  const [refreshReports, setRefreshReports] = useState(false);

  const refreshReportsList = () => {
    setRefreshReports((value) => !value);
  };

  let [isDeleteReportDailogOpen, setIsDeleteReportDailogOpen] = useState(false);
  let [selectedReportIdForDelete, setSelectedReportIdForDelete] = useState("");

  const closeDeleteRequestDialog = () => {
    setIsDeleteReportDailogOpen(false);
    setSelectedReportIdForDelete("");
  };

  const ReportListing = uploadedReportsList?.map(
    (report: {
      id: React.Key | null | undefined | string;
      fileFormat: string;
      url: string | undefined;
      name:
        | boolean
        | React.ReactChild
        | React.ReactFragment
        | React.ReactPortal
        | null
        | undefined;
    }) => (
      <div key={report.id}>
        <Grid item xs={5}>
          <Paper variant="outlined" className="p-1 m-1">
            <Grid container>
              <Grid item xs={1}>
                {report?.fileFormat === "png" ||
                report?.fileFormat === "jpg" ||
                report?.fileFormat === "jpeg" ? (
                  <img
                    style={{ width: "30px", height: "auto" }}
                    src={ImageFileIcon}
                    alt=""
                    className="img-fluid"
                  />
                ) : (
                  <PDFIcon />
                )}
              </Grid>
              <Grid item xs={10}>
                <a href={report.url} target="_blank" rel="noreferrer">
                  <label className="">{report.name}</label>
                </a>
              </Grid>
              <Grid item xs={1}>
                <DeleteOutlinedIcon
                  style={{ color: "lightgray", cursor: "pointer" }}
                  onClick={() => {
                    setSelectedReportIdForDelete(report.id?.toString() || "");
                    setIsDeleteReportDailogOpen(true);
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </div>
    ),
  );

  const { setSnackbar } = useSnackbarContext();

  const handleClose = () => {
    setShow(false);
    closeFn();
  };

  useEffect(() => {
    if (isFromAppointment) {
      fetchUploadedPatientsReports();
    }
  }, [refreshReports]);

  const fetchUploadedPatientsReports = () => {
    setIsGetReportsFetching(true);

    HttpClient.performGet(getReportsUrl)
      .then((response: HttpGetOutput<any>) => {
        setIsGetReportsFetching(false);
        if (!response?.body?.status?.success) {
          setSnackbar({
            message: response?.body?.status?.message,
            severity: "error",
          });
        } else {
          setUploadedReportsList(response?.body?.reports);
        }
      })
      .catch((e) => {
        setIsGetReportsFetching(false);
        setSnackbar({
          message: "Error occurred while loading reports. " + e.message,
          severity: "error",
        });
      });
  };

  const uploadReport =
    (sectionLabel: SectionLabel, apiReportType: string) =>
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files) {
        return;
      }
      setUploadingSection(sectionLabel);
      const file = e.target.files[0];
      const { type, name } = file;

      if (!allowedFileTypes.includes(type)) {
        setSnackbar({
          message: invalid_report_err_msg,
          severity: "error",
        });
        setUploadingSection(null);
        e.target.value = "";
        return;
      }

      HttpClient.performPost(
        isFromAppointment
          ? getReportUploadUrlFromAppointment
          : getReportUploadUrl,
        {
          reportType: apiReportType,
          note: "NA",
          name: name,
        },
      )
        .then((response: HttpMutationOutput<any>) => {
          if (!response?.body?.status?.success) {
            setSnackbar({
              message: response?.body?.status?.message,
              severity: "error",
            });
            setUploadingSection(null);
            return;
          }

          const uploadUrl = response?.body?.file?.signedUrl;
          if (!uploadUrl) {
            setSnackbar({
              message: "Upload url not found",
              severity: "error",
            });
            setUploadingSection(null);
            return;
          }

          fetch(uploadUrl, {
            method: "PUT",
            headers: {
              "Content-Type": type,
              "Access-Control-Allow-Origin": "*",
              "Cross-Origin-Resource-Policy": "cross-origin",
            },
            body: file,
          })
            .then((putResponse) => {
              setUploadingSection(null);
              e.target.value = "";
              if (putResponse?.ok) {
                setSnackbar({
                  message: `${sectionLabel} report uploaded`,
                  severity: "success",
                });
                refreshReportsList();
              } else {
                setSnackbar({
                  message: "Failed to upload report",
                  severity: "error",
                });
              }
            })
            .catch(() => {
              setUploadingSection(null);
              e.target.value = "";
              setSnackbar({
                message: "Failed to upload report",
                severity: "error",
              });
            });
        })
        .catch((e) => {
          setUploadingSection(null);
          e.target.value = "";
          setSnackbar({
            message: "Error occurred while uploading report. " + e.message,
            severity: "error",
          });
        });
    };

  return (
    <>
      <Dialog
        open={show}
        onClose={handleClose}
        fullWidth={true}
        maxWidth="md"
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Upload Reports
          <IconButton
            aria-label="close"
            onClick={handleClose}
            style={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <GrClose />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {REPORT_SECTIONS.map((section, index) => (
            <div className="upload_div" key={section.label}>
              <div className="heading">{section.label}</div>
              <div>
                <input
                  type="file"
                  id={`${section.label.replace(/\s/g, "")}ReportFile`}
                  accept="application/pdf, image/png, image/jpeg, image/jpg"
                  onChange={uploadReport(section.label, section.apiType)}
                  name={`${section.label}ReportFile`}
                />
              </div>
              <br />
              {uploadingSection === section.label && (
                <span>uploading...</span>
              )}
              {index < REPORT_SECTIONS.length - 1 && <hr />}
            </div>
          ))}
          {isFromAppointment && (
            <div className="upload_div">
              <div className="heading">Uploaded Reports</div>
              {isGetReportsFetching ? (
                "fetching reports..."
              ) : (
                <div>{ReportListing}</div>
              )}
            </div>
          )}

          <div style={{ marginBottom: "1em" }}></div>
        </DialogContent>
      </Dialog>
      {isDeleteReportDailogOpen && (
        <ConfirmDeleteReportDialog
          reportId={selectedReportIdForDelete}
          onDailogCanceledFn={() => {
            closeDeleteRequestDialog();
          }}
          onReportDeletedFn={() => {
            refreshReportsList();
            closeDeleteRequestDialog();
          }}
        />
      )}
    </>
  );
}
